import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Chip,
  Button,
  Skeleton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  DialogActions,
  Autocomplete,
  TextField,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  MenuItem,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { usePaginationState } from "../../../../hooks/usePaginationState";
import PaginationControls from "../../../shared/PaginationControls";
import { IoMdClose } from "react-icons/io";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";

import { AuditService } from "../../../../services/audit.service";
import { useAllUsers } from "../../../../queries/hooks";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { AUDIT_EVENT_OPTIONS } from "../../Audit/auditEventOptions";

interface NegotiationAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  budgetId?: string;
  negotiationVersion?: number;
  onImport?: (entries: AuditEntry[]) => void;
}

interface AuditEntry {
  _id: string;
  action: string;
  data: Record<string, any>;
  oldData: Record<string, any>;
  user: { name: string; email: string; _id?: string };
  date: string;
  createdAt?: string;
  area?: string;
}

interface FieldChange {
  field: string;
  oldValue: string;
  newValue: string;
  time: string;
}

const EXCLUDED_FIELDS = [
  "Criado em",
  "Atualizado em",
  "createdAt",
  "updatedAt",
  "_id",
  "__v",
  "deletedAt",
  "Deletado em",
  "Último usuário que atualizou",
  "isApiUpdate",
  "actionType",
  "budgetServices",
  "Serviços do budget",
  "budgetId",
  "ID do Orçamento",
  "budgetName",
  "Nome do Orçamento",
  "negotiationVersion",
  "Versão de negociação",
  "closingVersion",
  "Versão de fechamento",
  "serviceChanges",
  "Alterações de serviços",
  "serviceFieldChanges",
];

const MAX_SUPPLIER_LENGTH = 30;
const truncateSupplier = (name: string) => {
  if (!name) return "-";
  return name.length > MAX_SUPPLIER_LENGTH
    ? name.substring(0, MAX_SUPPLIER_LENGTH) + "..."
    : name;
};

const FRIENDLY_KEYS = ["label", "Nome", "name", "descricao", "description", "title", "valor", "value"];

const actionDict: { [key: string]: string } = {
  Update: "atualizou",
  Create: "criou",
  Delete: "excluiu",
  StatusChange: "alterou o status",
  DUPLICATE: "duplicou",
  DELETE: "excluiu",
  DELETED: "excluiu",
  RENAME: "renomeou",
  NEGOTIATE: "enviou para negociação",
  SEND_TO_NEGOTIATION: "enviou para negociação",
  APPROVED: "aprovou",
  NEGOTIATION_APPROVED: "aprovou negociação",
  DENIED: "reprovou",
  NEGOTIATION_DENIED: "reprovou negociação",
  NEW_VERSION: "criou nova versão",
  NEGOTIATION_VERSION_CREATED: "criou nova versão de negociação",
  APPROVE: "enviou para fechamento",
  APROVE: "aprovou para fechamento",
  SAVE: "salvou",
  NEGOTIATION_UPDATED: "atualizou negociação",
  CONSULTING: "criou uma versão Assessoria",
  VersionCreated: "criou nova versão",
  NegotiationServiceUpdate: "atualizou serviços de negociação",
  NegotiationPaymentSolicitationUpdate: "atualizou solicitação de pagamento",
  NegotiationFilesUpdate: "atualizou arquivos",
  Export: "exportou",
};

const negotiationStatusDict: { [key: string]: { label: string; color: string } } = {
  NEGOTIATE: { label: "Em Negociação", color: "#FFA726" },
  APPROVED: { label: "Aprovada", color: "#66BB6A" },
  DENIED: { label: "Reprovada", color: "#EF5350" },
  OPEN: { label: "Aberta", color: "#42A5F5" },
};

const truncateText = (text: string) => text;

const formatObject = (obj: any): string => {
  if (!obj) return "(Vazio)";

  for (const key of FRIENDLY_KEYS) {
    if ((obj as any)[key]) return truncateText(String((obj as any)[key]));
  }

  const parts = Object.entries(obj)
    .filter(([key, val]) => !EXCLUDED_FIELDS.includes(key) && val !== undefined)
    .map(([key, val]) => `${key}: ${formatPrimitive(val)}`);

  const combined = parts.join("\n");
  return truncateText(combined || "[Objeto]");
};

const formatPrimitive = (value: any): string => {
  if (value === null || value === undefined) return "(Vazio)";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number" || typeof value === "string") {
    const str = String(value);
    return str.trim() === "" ? "(Vazio)" : str;
  }
  if (Array.isArray(value)) {
    const mapped = value.map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return `• ${formatObject(item)}`;
      }
      return `• ${formatPrimitive(item)}`;
    });
    return mapped.join("\n");
  }
  if (typeof value === "object") {
    return formatObject(value);
  }
  return String(value);
};

const formatValue = (value: any): string => formatPrimitive(value);

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "---";
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return "---";
  }
};

const formatTime = (dateStr: string): string => {
  if (!dateStr) return "---";
  try {
    const date = new Date(dateStr);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "---";
  }
};

const NegotiationAuditModal = ({
  isOpen,
  onClose,
  budgetId,
  negotiationVersion,
  onImport,
}: NegotiationAuditModalProps) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId") || "";
  const { activeBudget, versionInfo } = useBudget();

  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState(0);

  const { data: usersData } = useAllUsers();

  const { data: rawAuditData, isLoading, isError } = useQuery({
    queryKey: ["negotiation-audit-data", projectId, selectedUser, selectedAction],
    queryFn: () =>
      AuditService.getByProjectAndArea({
        project: projectId,
        area: "negotiation",
        user: selectedUser || null,
        action: selectedAction || undefined,
      }),
    enabled: !!projectId && isOpen,
  });

  // Filter audit entries based on negotiation-related actions
  const negotiationActions = [
    "NEGOTIATE",
    "SEND_TO_NEGOTIATION",
    "NEGOTIATION_APPROVED",
    "NEGOTIATION_DENIED",
    "NEGOTIATION_VERSION_CREATED",
    "NEGOTIATION_UPDATED",
    "APPROVED",
    "DENIED",
    "NEW_VERSION",
    "NegotiationServiceUpdate",
    "NegotiationPaymentSolicitationUpdate",
    "NegotiationFilesUpdate",
  ];

  const auditEntries: AuditEntry[] = useMemo(() => {
    if (!rawAuditData || !Array.isArray(rawAuditData)) return [];

    const negotiationTransitionActionSet = new Set([
      "NEGOTIATE",
      "SEND_TO_NEGOTIATION",
      "NEGOTIATION_APPROVED",
      "NEGOTIATION_DENIED",
      "NEGOTIATION_VERSION_CREATED",
      "NEGOTIATION_UPDATED",
      "APPROVED",
      "DENIED",
      "NEW_VERSION",
    ]);

    const filtered = rawAuditData.filter((entry: AuditEntry) => {
      const entryBudgetId = entry.data?.budgetId || entry.data?.["ID do Orçamento"];
      const currentBudgetId = budgetId || activeBudget?._id;

      if (currentBudgetId && entryBudgetId && entryBudgetId !== currentBudgetId) {
        return false;
      }

      const entryArea = entry.area || "";
      const isNegotiationArea =
        entryArea === "project_negotiation" ||
        entryArea === "negotiation" ||
        entryArea === "negotiation_services";
      const isNegotiationTransition =
        entryArea === "project_budget" &&
        negotiationTransitionActionSet.has(entry.action);

      return isNegotiationArea || isNegotiationTransition;
    });

    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.date || a.createdAt).getTime();
      const dateB = new Date(b.date || b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [rawAuditData, budgetId, activeBudget]);

  // Group entries by version if available
  const entriesByVersion = useMemo(() => {
    const grouped: { [key: string]: AuditEntry[] } = {
      all: auditEntries,
    };

    auditEntries.forEach((entry) => {
      // Priorizar a versão do data (novo valor), fallback para oldData se não existir
      let version =
        entry.data?.["Versão de negociação"] ??
        entry.data?.negotiationVersion ??
        entry.oldData?.["Versão de negociação"] ??
        entry.oldData?.negotiationVersion ??
        null;
      
      // Se a versão ainda for null, marcar como sem_versao
      if (version === null || version === undefined) {
        version = "sem_versao";
      }
      
      const versionKey = `v${version}`;
      if (!grouped[versionKey]) {
        grouped[versionKey] = [];
      }
      grouped[versionKey].push(entry);
    });

    return grouped;
  }, [auditEntries]);

  const validNegotiationTabs = useMemo(() => {
    const history = activeBudget?.versionHistory || [];
    let consultingCount = 0;

    return history
      .filter((vh: any) => {
        if (vh.version === 0) return false;
        if (vh.isConsulting) return true;
        return vh.status === "NEGOTIATE";
      })
      .sort((a: any, b: any) => a.version - b.version)
      .map((vh: any) => {
        if (vh.isConsulting) {
          consultingCount += 1;
          return {
            key: `v${vh.version}`,
            label: "Assessoria",
            rawVersion: vh.version,
            isConsulting: true,
          };
        }

        const displayNumber = vh.version - consultingCount;
        return {
          key: `v${vh.version}`,
          label: `Negociação ${displayNumber}`,
          rawVersion: vh.version,
          isConsulting: false,
        };
      })
      .filter((tab) => entriesByVersion[tab.key]?.length > 0);
  }, [activeBudget, entriesByVersion]);

  const currentEntries = useMemo(() => {
    if (activeTab === 0) return auditEntries;
    const versionKey = validNegotiationTabs[activeTab - 1]?.key;
    return entriesByVersion[versionKey] || [];
  }, [activeTab, auditEntries, entriesByVersion, validNegotiationTabs]);

  const {
    page,
    setPage,
    rowsPerPage,
    paginatedData: paginatedEntries,
    totalPages,
    rangeLabel,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginationState(currentEntries);

  const countChangedFields = (entry: AuditEntry): number => {
    const data = entry.data || {};
    if (Array.isArray(data.serviceFieldChanges) && data.serviceFieldChanges.length > 0) {
      return data.serviceFieldChanges.length;
    }
    return Object.keys(data).filter((key) => !EXCLUDED_FIELDS.includes(key)).length;
  };

  const getFieldChanges = (entry: AuditEntry): any[] => {
    const changes: any[] = [];
    const newData = entry.data || {};
    const oldData = entry.oldData || {};
    const dateStr = entry.date || entry.createdAt || "";

    if (Array.isArray(newData.serviceFieldChanges) && newData.serviceFieldChanges.length > 0) {
      for (const sfc of newData.serviceFieldChanges) {
        changes.push({
          isServiceChange: true,
          service: sfc.serviceType || "-",
          supplier: sfc.supplierName || "",
          field: sfc.field || "",
          oldValue: formatValue(sfc.oldValue),
          newValue: formatValue(sfc.newValue),
          time: formatTime(dateStr),
        });
      }
      return changes;
    }

    for (const key of Object.keys(newData)) {
      if (EXCLUDED_FIELDS.includes(key)) continue;
      const oldValue = oldData[key];
      const newValue = newData[key];
      changes.push({
        field: key,
        oldValue: formatValue(oldValue),
        newValue: formatValue(newValue),
        time: formatTime(dateStr),
      });
    }

    for (const key of Object.keys(oldData)) {
      if (EXCLUDED_FIELDS.includes(key)) continue;
      if (!(key in newData)) {
        changes.push({
          field: key,
          oldValue: formatValue(oldData[key]),
          newValue: "(Removido)",
          time: formatTime(dateStr),
        });
      }
    }

    return changes;
  };

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setPage(1);
  };

  const handleImport = () => {
    if (!onImport) return;
    onImport(currentEntries);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setExpandedRows(new Set());
  };

  const selectedUserName = useMemo(() => {
    if (!selectedUser || !usersData) return null;
    const user = usersData.find((u: any) => u._id === selectedUser);
    return user?.name || null;
  }, [selectedUser, usersData]);

  const getStatusChip = (entry: AuditEntry) => {
    const status = entry.data?.status || entry.data?.Status;
    const statusInfo = negotiationStatusDict[status];

    if (!statusInfo) return null;

    return (
      <Chip
        size="small"
        label={statusInfo.label}
        sx={{
          bgcolor: statusInfo.color,
          color: "white",
          fontSize: "0.7rem",
          height: 20,
          ml: 1,
        }}
      />
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setExpandedRows(new Set());
      setPage(1);
      setActiveTab(0);
    }
  }, [isOpen, setPage]);

  const versionTabs = validNegotiationTabs;

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "auto",
          width: { xs: "90vw", md: "900px" },
        },
      }}
      aria-modal="true"
      role="dialog"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid #E0E0E0",
          bgcolor: "#F5F9FF",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" color="#1361A4" fontWeight="bold">
            Auditoria - Negociação
          </Typography>
          {/* {versionInfo && (
            <Chip
              size="small"
              label={`Versão ${versionInfo.version + 1}`}
              sx={{ bgcolor: "#E3F2FD", color: "#1361A4" }}
            />
          )} */}
        </Box>
        <IconButton aria-label="Fechar" onClick={onClose} sx={{ color: "#666" }}>
          <IoMdClose size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "70vh",
          gap: 2,
        }}
      >
        {/* Version Tabs */}
        {versionTabs.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="Versões de negociação"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`Todas (${auditEntries.length})`} />
              {versionTabs.map((versionTab: any) => {
                const versionEntriesCount = entriesByVersion[versionTab.key]?.length || 0;
                const budgetName = activeBudget?.name || "Orçamento";
                return (
                  <Tab 
                    key={versionTab.key} 
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                          {budgetName}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                          {versionTab.label} ({versionEntriesCount})
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
          </Box>
        )}

        {/* User Filter */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2" color="textSecondary">
            Filtrar por usuário
          </Typography>

          <Autocomplete
            size="small"
            sx={{ minWidth: 220, maxWidth: 320 }}
            options={(usersData || []).filter((user: any) => user.active)}
            getOptionLabel={(option: any) => option.name || ""}
            value={(usersData || []).find((u: any) => u._id === selectedUser) || null}
            onChange={(_: any, value: any) => {
              setSelectedUser(value?._id || null);
              setPage(1);
            }}
            clearOnEscape
            renderInput={(params: any) => (
              <TextField {...params} placeholder="Todos os usuários" variant="outlined" />
            )}
            noOptionsText="Nenhum usuário encontrado"
            isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
          />

          {selectedUser && (
            <Chip
              label={selectedUserName}
              onDelete={handleClearUser}
              deleteIcon={<IoMdClose />}
              sx={{
                bgcolor: "#E3F2FD",
                color: "#1361A4",
                "& .MuiChip-deleteIcon": { color: "#1361A4" },
              }}
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2" color="textSecondary">
            Tipo de evento
          </Typography>
          <TextField
            select
            size="small"
            sx={{ minWidth: 220 }}
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            SelectProps={{ displayEmpty: true }}
          >
            {AUDIT_EVENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Erro ao carregar dados de auditoria de negociação. Tente novamente.
          </Alert>
        )}

        {/* Audit List */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #E0E0E0",
            borderRadius: 1,
            bgcolor: "#FAFAFA",
            maxHeight: "45vh",
            minHeight: 200,
          }}
        >
          {isLoading ? (
            <Box p={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : isError ? (
            <Box p={3} textAlign="center">
              <Typography color="error">
                Erro ao carregar dados de auditoria. Tente novamente.
              </Typography>
            </Box>
          ) : paginatedEntries.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                Nenhum registro de negociação encontrado
                {selectedUser ? " para o usuário selecionado" : ""}.
              </Typography>
            </Box>
          ) : (
            <Box>
              {paginatedEntries.map((entry: AuditEntry, index: number) => {
                const globalIndex = (page - 1) * rowsPerPage + index;
                const isExpanded = expandedRows.has(globalIndex);
                const fieldCount = countChangedFields(entry);
                const actionVerb = actionDict[entry.action] || entry.action;
                const dateFormatted = formatDate(entry.date || entry.createdAt || "");
                const userName = entry.user?.name || "Usuário desconhecido";

                return (
                  <Box
                    key={entry._id || globalIndex}
                    sx={{
                      borderBottom: "1px solid #E0E0E0",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      onClick={() => toggleRow(globalIndex)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        cursor: "pointer",
                        bgcolor: isExpanded ? "#F0F7FF" : "transparent",
                        "&:hover": { bgcolor: "#F0F7FF" },
                        transition: "background-color 0.2s",
                      }}
                      role="button"
                      aria-expanded={isExpanded}
                      tabIndex={0}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          toggleRow(globalIndex);
                          e.preventDefault();
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {isExpanded ? (
                          <FaChevronUp color="#1361A4" size={12} />
                        ) : (
                          <FaChevronDown color="#666" size={12} />
                        )}
                        <Typography variant="body2">
                          <strong>{userName}</strong>{" "}
                          <Typography component="span" sx={{ color: "#1361A4", fontWeight: 500 }}>
                            {actionVerb}
                          </Typography>{" "}
                          {fieldCount > 0 && (
                            <>
                              <strong>{fieldCount}</strong> campo{fieldCount > 1 ? "s" : ""}
                            </>
                          )}{" "}
                          em {dateFormatted}
                        </Typography>
                        {getStatusChip(entry)}
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {formatTime(entry.date || entry.createdAt || "")}
                      </Typography>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box
                        sx={{
                          px: 2,
                          pb: 2,
                          bgcolor: "#FFF",
                          borderTop: "1px dashed #CCC",
                          overflowX: "auto",
                          maxHeight: 320,
                          overflowY: "auto",
                        }}
                      >
                        {(() => {
                          const fieldChanges = getFieldChanges(entry);
                          const isService = fieldChanges.length > 0 && fieldChanges[0].isServiceChange;
                          if (isService) {
                            return (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Serviço</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Fornecedor</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: "16%" }}>Campo</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Alterado de</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Para</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: "12%" }}>Alterado às</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {fieldChanges.map((change: any, idx: number) => (
                                    <TableRow key={idx}>
                                      <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                        {change.service || "-"}
                                      </TableCell>
                                      <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                        <Tooltip title={change.supplier || ""} arrow placement="top">
                                          <span>{truncateSupplier(change.supplier)}</span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                        {change.field}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          wordBreak: "break-word",
                                          whiteSpace: "pre-wrap",
                                          color: change.oldValue === "(Vazio)" ? "#999" : "inherit",
                                        }}
                                      >
                                        {change.oldValue}
                                      </TableCell>
                                      <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                        {change.newValue}
                                      </TableCell>
                                      <TableCell>{change.time}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            );
                          }
                          return (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: "bold", width: "25%" }}>Campo</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Valor Anterior</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Novo Valor</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", width: "15%" }}>Horário</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {fieldChanges.map((change: any, idx: number) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                      {change.field}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: "break-word",
                                        whiteSpace: "pre-wrap",
                                        color: change.oldValue === "(Vazio)" ? "#999" : "inherit",
                                      }}
                                    >
                                      {change.oldValue}
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                      {change.newValue}
                                    </TableCell>
                                    <TableCell>{change.time}</TableCell>
                                  </TableRow>
                                ))}
                                {fieldChanges.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={4} align="center">
                                      <Typography variant="body2" color="textSecondary">
                                        Nenhum detalhe de campo disponível
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          );
                        })()}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Pagination */}
        {currentEntries.length > 0 && (
          <PaginationControls
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            rangeLabel={rangeLabel}
            onPageChange={(e, value) => {
              handlePageChange(e, value);
              setExpandedRows(new Set());
            }}
            onRowsPerPageChange={(value) => {
              handleRowsPerPageChange(value);
              setExpandedRows(new Set());
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E0E0E0" }}>
        <Button variant="outlined" onClick={onClose}>
          Fechar
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={isLoading || isError || currentEntries.length === 0}
        >
          Exportar Relatório
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NegotiationAuditModal;
